<template>
  <div class="stats-container">
    <div class="stats-header">
      <h1>Album Ratings Stats</h1>
      <p v-if="loading">Loading data...</p>
      <p v-else-if="albums.length">Analyzing {{ albums.length }} albums</p>
    </div>

    <div v-if="!loading && albums.length" class="charts-grid">
      <!-- Tier Distribution + Reviewer Average (paired) -->
      <div class="chart-card">
        <h2>Tier Distribution</h2>
        <Pie :data="tierDistributionData" :options="chartOptions" />
      </div>

      <div class="chart-card">
        <h2>Reviewer Average Tiers</h2>
        <Bar :data="reviewerComparisonData" :options="barChartOptions" />
      </div>

      <!-- Reviews Over Time (full width) -->
      <div class="chart-card full-width">
        <h2>Reviews Timeline</h2>
        <Line :data="timelineData" :options="timelineOptions" />
      </div>

      <!-- Individual Reviewer Distributions (full width) -->
      <div class="chart-card full-width">
        <h2>Tiers by Reviewer</h2>
        <Bar :data="reviewerTierData" :options="stackedBarOptions" />
      </div>

      <!-- Fun Stats -->
      <div class="stats-summary full-width">
        <h2>Quick Stats</h2>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-value">{{ totalAlbums }}</span>
            <span class="stat-label">Total Albums</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ totalSessions }}</span>
            <span class="stat-label">Sessions<span v-if="firstSessionDate"> · since {{ firstSessionDate }}</span></span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ mostCommonTier }}</span>
            <span class="stat-label">Most Common Tier</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ mostGenerousReviewer }}</span>
            <span class="stat-label">Most Generous</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ harshestReviewer }}</span>
            <span class="stat-label">Most Critical</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{{ mostSubmissions.name }}</span>
            <span class="stat-label">Most Submissions ({{ mostSubmissions.count }})</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title } from 'chart.js';
import { Pie, Line, Bar } from 'vue-chartjs';
import axios from 'axios';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title);

export default {
  name: 'StatsView',
  components: {
    Pie,
    Line,
    Bar
  },
  setup() {
    const albums = ref([]);
    const totalSessions = ref(0);
    const firstSessionDate = ref(null);
    const loading = ref(true);
    const error = ref(null);

    const tierOrder = ['S', 'A', 'B', 'C', 'D', 'E', 'F'];
    const tierValues = { 'S': 6, 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'E': 1, 'F': 0 };
    const tierColors = {
      'S': '#f00000',
      'A': '#ff7d00',
      'B': '#ffe902',
      'C': '#32cd32',
      'D': '#00bfff',
      'E': '#708090',
      'F': '#555555'
    };

    const fetchAlbums = async () => {
      try {
        loading.value = true;
        const apiUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:3000/api/albums'
          : '/api/albums';

        const response = await axios.get(apiUrl, {
          params: {
            page: 1,
            limit: 1000,
            include_test_albums: false
          },
          headers: {
            'x-api-key': process.env.VUE_APP_API_KEY
          }
        });

        albums.value = (response.data.data || []).filter(album => !album.test_mode);
        loading.value = false;
      } catch (err) {
        console.error('Error fetching albums:', err);
        error.value = 'Failed to load album data';
        loading.value = false;
      }
    };

    // Tier Distribution
    const tierDistribution = computed(() => {
      const dist = {};
      tierOrder.forEach(tier => dist[tier] = 0);

      albums.value.forEach(album => {
        if (album.tier && album.tier !== 'unknown') {
          dist[album.tier] = (dist[album.tier] || 0) + 1;
        }
      });

      return dist;
    });

    const tierDistributionData = computed(() => ({
      labels: tierOrder,
      datasets: [{
        data: tierOrder.map(tier => tierDistribution.value[tier] || 0),
        backgroundColor: tierOrder.map(tier => tierColors[tier]),
        borderWidth: 2,
        borderColor: '#1a1a1a'
      }]
    }));

    // Timeline Data
    const timelineData = computed(() => {
      const albumsByMonth = {};

      albums.value.forEach(album => {
        if (album.reviewed_at && album.reviewed_at !== 'unknown') {
          const date = new Date(album.reviewed_at);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

          if (!albumsByMonth[monthKey]) {
            albumsByMonth[monthKey] = { S: 0, A: 0, B: 0, C: 0, D: 0, E: 0, F: 0 };
          }

          if (album.tier && album.tier !== 'unknown') {
            albumsByMonth[monthKey][album.tier]++;
          }
        }
      });

      const sortedMonths = Object.keys(albumsByMonth).sort();

      return {
        labels: sortedMonths.map(m => {
          const [year, month] = m.split('-');
          return new Date(year, month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        }),
        datasets: tierOrder.map(tier => ({
          label: tier,
          data: sortedMonths.map(month => albumsByMonth[month][tier] || 0),
          borderColor: tierColors[tier],
          backgroundColor: tierColors[tier] + '80',
          tension: 0.3,
          fill: true
        }))
      };
    });

    // Reviewer Comparison
    const reviewerStats = computed(() => {
      const reviewers = ['arno', 'lucas', 'thomas', 'tyrone', 'wouter'];
      const stats = {};

      reviewers.forEach(reviewer => {
        stats[reviewer] = { sum: 0, count: 0, tiers: { S: 0, A: 0, B: 0, C: 0, D: 0, E: 0, F: 0 } };
      });

      albums.value.forEach(album => {
        if (album.individual_tiers) {
          Object.entries(album.individual_tiers).forEach(([reviewer, tier]) => {
            if (stats[reviewer]) {
              stats[reviewer].sum += tierValues[tier] || 0;
              stats[reviewer].count++;
              stats[reviewer].tiers[tier]++;
            }
          });
        }
      });

      return stats;
    });

    const reviewerComparisonData = computed(() => {
      const reviewers = Object.keys(reviewerStats.value);

      return {
        labels: reviewers.map(r => r.charAt(0).toUpperCase() + r.slice(1)),
        datasets: [{
          label: 'Average Tier Score',
          data: reviewers.map(r => {
            const stats = reviewerStats.value[r];
            return stats.count > 0 ? (stats.sum / stats.count).toFixed(2) : 0;
          }),
          backgroundColor: '#4CAF50',
          borderColor: '#2E7D32',
          borderWidth: 2
        }]
      };
    });

    // Reviewer Tier Distribution
    const reviewerTierData = computed(() => {
      const reviewers = Object.keys(reviewerStats.value);

      return {
        labels: reviewers.map(r => r.charAt(0).toUpperCase() + r.slice(1)),
        datasets: tierOrder.map(tier => ({
          label: tier,
          data: reviewers.map(r => reviewerStats.value[r].tiers[tier]),
          backgroundColor: tierColors[tier],
          borderWidth: 1,
          borderColor: '#1a1a1a'
        }))
      };
    });

    // Quick Stats
    const totalAlbums = computed(() => albums.value.length);

    const mostCommonTier = computed(() => {
      const dist = tierDistribution.value;
      let maxTier = 'S';
      let maxCount = 0;

      Object.entries(dist).forEach(([tier, count]) => {
        if (count > maxCount) {
          maxCount = count;
          maxTier = tier;
        }
      });

      return maxTier;
    });

    const mostGenerousReviewer = computed(() => {
      const reviewers = Object.keys(reviewerStats.value);
      let highest = { name: '', avg: 0 };

      reviewers.forEach(reviewer => {
        const stats = reviewerStats.value[reviewer];
        const avg = stats.count > 0 ? stats.sum / stats.count : 0;
        if (avg > highest.avg) {
          highest = { name: reviewer, avg };
        }
      });

      return highest.name.charAt(0).toUpperCase() + highest.name.slice(1);
    });

    const harshestReviewer = computed(() => {
      const reviewers = Object.keys(reviewerStats.value);
      let lowest = { name: '', avg: 100 };

      reviewers.forEach(reviewer => {
        const stats = reviewerStats.value[reviewer];
        const avg = stats.count > 0 ? stats.sum / stats.count : 100;
        if (avg < lowest.avg) {
          lowest = { name: reviewer, avg };
        }
      });

      return lowest.name.charAt(0).toUpperCase() + lowest.name.slice(1);
    });

    const mostSubmissions = computed(() => {
      const submissions = {};

      albums.value.forEach(album => {
        if (album.submitted_by) {
          submissions[album.submitted_by] = (submissions[album.submitted_by] || 0) + 1;
        }
      });

      let max = { name: 'N/A', count: 0 };
      Object.entries(submissions).forEach(([name, count]) => {
        if (count > max.count) {
          max = { name, count };
        }
      });

      return max;
    });

    const labelColor = 'rgba(243, 243, 243, 0.7)';
    const gridColor = 'rgba(255, 255, 255, 0.06)';

    // Chart Options
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: labelColor,
            font: { size: 12 },
            padding: 16
          }
        }
      }
    };

    const timelineOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: labelColor,
            font: { size: 11 },
            padding: 16
          }
        }
      },
      scales: {
        x: {
          ticks: { color: labelColor },
          grid: { color: gridColor }
        },
        y: {
          ticks: { color: labelColor, stepSize: 1 },
          grid: { color: gridColor }
        }
      }
    };

    const barChartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        x: {
          ticks: { color: labelColor },
          grid: { color: gridColor }
        },
        y: {
          min: 0,
          max: 6,
          ticks: {
            color: labelColor,
            callback: function(value) {
              const labels = ['F', 'E', 'D', 'C', 'B', 'A', 'S'];
              return labels[value] || value;
            }
          },
          grid: { color: gridColor }
        }
      }
    };

    const stackedBarOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: labelColor,
            font: { size: 11 },
            padding: 16
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          ticks: { color: labelColor },
          grid: { color: gridColor }
        },
        y: {
          stacked: true,
          ticks: { color: labelColor },
          grid: { color: gridColor }
        }
      }
    };

    const fetchSessions = async () => {
      try {
        const apiUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:3000/api/sessions'
          : '/api/sessions';

        const response = await axios.get(apiUrl, {
          params: { limit: 500 },
          headers: { 'x-api-key': process.env.VUE_APP_API_KEY }
        });

        const sessions = response.data.data || [];
        totalSessions.value = response.data.pagination?.total ?? sessions.length;

        const dates = sessions
          .map(s => new Date(s.session_date))
          .filter(d => !isNaN(d));

        if (dates.length) {
          const earliest = new Date(Math.min(...dates));
          firstSessionDate.value = earliest.toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric'
          });
        }
      } catch (err) {
        console.error('Error fetching sessions:', err);
      }
    };

    onMounted(() => {
      fetchAlbums();
      fetchSessions();
    });

    return {
      albums,
      loading,
      error,
      tierDistributionData,
      timelineData,
      reviewerComparisonData,
      reviewerTierData,
      chartOptions,
      timelineOptions,
      barChartOptions,
      stackedBarOptions,
      totalAlbums,
      totalSessions,
      firstSessionDate,
      mostCommonTier,
      mostGenerousReviewer,
      harshestReviewer,
      mostSubmissions
    };
  }
};
</script>

<style scoped>
.stats-container {
  padding: 24px 20px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  color: #f3f3f3;
}

.stats-header {
  text-align: center;
  margin-bottom: 40px;
}

.stats-header h1 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px;
  color: #f3f3f3;
  letter-spacing: -0.025em;
}

.stats-header p {
  color: rgba(243, 243, 243, 0.6);
  font-size: 1rem;
  margin: 0;
}

.charts-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

@media (min-width: 768px) {
  .charts-grid {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
}

@media (min-width: 1200px) {
  .stats-container {
    padding: 36px 48px;
  }

  .stats-header {
    margin-bottom: 48px;
  }

  .stats-header h1 {
    font-size: 2.5rem;
  }

  .charts-grid {
    gap: 28px;
  }
}

.chart-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.chart-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.15);
}

.full-width {
  grid-column: 1 / -1;
}

.chart-card h2 {
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(243, 243, 243, 0.9);
  letter-spacing: 0.01em;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  color: rgba(243, 243, 243, 0.5);
}

@media (min-width: 1200px) {
  .chart-card {
    padding: 32px;
  }
}

.stats-summary {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.stats-summary h2 {
  margin: 0 0 24px 0;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  color: rgba(243, 243, 243, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (min-width: 1200px) {
  .stats-grid {
    gap: 16px;
  }
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.stat-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #f3f3f3;
  margin-bottom: 6px;
  letter-spacing: -0.02em;
}

@media (min-width: 1200px) {
  .stat-value {
    font-size: 2.2rem;
  }
}

.stat-label {
  font-size: 0.8rem;
  color: rgba(243, 243, 243, 0.5);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.error-message {
  text-align: center;
  color: #ef4444;
  padding: 20px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  margin-top: 20px;
}
</style>
